import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Info, MapPin, Share2, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { parseDateString } from '../lib/utils';

// Interface for age groups from the database
interface AgeGroup {
  id: string;
  group_name: string;
  sex: 'male' | 'female';
  birth_date_min?: string;
  birth_date_max?: string;
  created_at: string;
}

// Interface for evaluation sessions from the database
interface EvaluationSession {
  id: string;
  session_date: string;
  start_time: string;
  end_time: string;
  location_name: string | null;
  location_address: string | null;
  instructions: string | null;
  special_notes: string | null;
  created_at: string;
}

// Interface for the join table between sessions and age groups
interface EvaluationSessionAgeGroup {
  id: string;
  evaluation_session_id: string;
  age_group_id: string;
  created_at: string;
}

// Frontend interfaces for rendering
interface TryoutLocation {
  name: string;
  address: string;
}

interface TryoutSession {
  id: string;
  date: string;
  time: string;
  location: TryoutLocation;
  notes?: string;
  special_notes?: string | null;
}

interface TryoutEvent {
  ageGroups: string[]; // Group names for display
  ageGroupIds: string[]; // Group IDs for filtering
  sex: 'male' | 'female';
  birthDateRange?: string; // Formatted birth date range for display
  sessions: TryoutSession[];
}

// Helper function to format a birth date range for display
function formatBirthDateRange(birthDateMin?: string, birthDateMax?: string): string | undefined {
  if (!birthDateMin || !birthDateMax) return undefined;
  const fmt = (dateStr: string) => {
    const d = parseDateString(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  return `Born ${fmt(birthDateMin)} – ${fmt(birthDateMax)}`;
}

const TryoutSchedule: React.FC = () => {
  // Function to create and copy shareable link
  const handleShareLink = (ageGroupId: string) => {
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/tryout-info?age_group_id=${ageGroupId}`;
    
    // Copy the URL to clipboard
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setCopySuccess(ageGroupId);
        setTimeout(() => setCopySuccess(null), 2000); // Reset after 2 seconds
      })
      .catch(err => {
        console.error('Error copying text: ', err);
      });
  };
  const [activeTab, setActiveTab] = useState<'male' | 'female'>('male');
  // Reset selected age group when changing tabs to ensure filter is always relevant
  useEffect(() => {
    setSelectedAgeGroup('all');
  }, [activeTab]);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('all'); // 'all' or age group ID
  const [tryoutEvents, setTryoutEvents] = useState<TryoutEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [availableAgeGroups, setAvailableAgeGroups] = useState<{male: AgeGroup[], female: AgeGroup[]}>({male: [], female: []});
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  // Function to fetch tryout schedule data from Supabase
  const fetchTryoutSchedule = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Step 1: Fetch all evaluation sessions
      const { data: sessions, error: sessionsError } = await supabase
        .from('evaluation_sessions')
        .select('*')
        .order('session_date', { ascending: true });
      
      if (sessionsError) throw new Error(`Error fetching sessions: ${sessionsError.message}`);
      if (!sessions) throw new Error('No sessions found');
      
      // Step 2: Fetch all age groups
      const { data: ageGroups, error: ageGroupsError } = await supabase
        .from('age_groups')
        .select('*');
      
      if (ageGroupsError) throw new Error(`Error fetching age groups: ${ageGroupsError.message}`);
      if (!ageGroups) throw new Error('No age groups found');
      
      // Group age groups by sex and sort alphabetically
      const maleAgeGroups = ageGroups.filter(group => group.sex === 'male')
        .sort((a, b) => a.group_name.localeCompare(b.group_name));
      const femaleAgeGroups = ageGroups.filter(group => group.sex === 'female')
        .sort((a, b) => a.group_name.localeCompare(b.group_name));
      
      setAvailableAgeGroups({ male: maleAgeGroups, female: femaleAgeGroups });
      
      // Step 3: Fetch the join table data
      const { data: sessionAgeGroups, error: joinError } = await supabase
        .from('evaluation_session_age_groups')
        .select('*');
      
      if (joinError) throw new Error(`Error fetching join table: ${joinError.message}`);
      if (!sessionAgeGroups) throw new Error('No session-age group relationships found');
      
      // Step 4: Process the data to match our frontend model
      const processedEvents = processDataForFrontend(sessions, ageGroups, sessionAgeGroups);
      setTryoutEvents(processedEvents);
    } catch (error) {
      console.error('Error fetching tryout schedule:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTryoutSchedule();
  }, []);

  return (
    <div className="mt-12 mb-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Placement Schedule</h2>
      
      {/* Tabs for Male/Female */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('male')}
          className={`py-3 px-6 font-medium text-sm ${
            activeTab === 'male'
              ? 'border-b-2 border-indigo-500 text-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Boys Teams
        </button>
        <button
          onClick={() => setActiveTab('female')}
          className={`py-3 px-6 font-medium text-sm ${
            activeTab === 'female'
              ? 'border-b-2 border-indigo-500 text-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Girls Teams
        </button>
      </div>

      {/* Age Group Filter */}
      <div className="mb-6">
        <label htmlFor="age-group" className="block text-sm font-medium text-gray-700 mb-2">
          Filter by Age Group:
        </label>
        <select
          id="age-group"
          value={selectedAgeGroup}
          onChange={(e) => setSelectedAgeGroup(e.target.value)}
          className="w-full md:w-64 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="all">All Age Groups</option>
          
          {/* Show only age groups that match the active tab */}
          {availableAgeGroups[activeTab].length > 0 && (
            <optgroup label={activeTab === 'male' ? 'Boys' : 'Girls'}>
              {availableAgeGroups[activeTab].map((group) => {
                const range = formatBirthDateRange(group.birth_date_min, group.birth_date_max);
                return (
                  <option key={group.id} value={group.id}>
                    {group.group_name}{range ? ` (${range})` : ''}
                  </option>
                );
              })}
            </optgroup>
          )}
        </select>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {/* Schedule Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {!loading && tryoutEvents.filter(tryout => {
          // Filter by sex tab
          if (tryout.sex !== activeTab) return false;
          
          // Filter by age group ID
          if (selectedAgeGroup !== 'all') {
            // Check if this tryout includes the selected age group
            return tryout.ageGroupIds.includes(selectedAgeGroup);
          }
          
          return true;
        }).map((tryout, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-indigo-500 hover:shadow-lg transition duration-300">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    {tryout.ageGroups.join(' / ')} {tryout.sex === 'male' ? 'Boys' : 'Girls'}
                  </h3>
                  {tryout.birthDateRange && (
                    <p className="text-sm text-gray-500 mt-0.5">{tryout.birthDateRange}</p>
                  )}
                </div>
                <button
                  onClick={() => handleShareLink(tryout.ageGroupIds[0])}
                  className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                  title="Share Link to this Age Group"
                >
                  {copySuccess === tryout.ageGroupIds[0] ? 
                    <><Check className="h-4 w-4 mr-1" /> Copied!</> : 
                    <><Share2 className="h-4 w-4 mr-1" /> Share</>}
                </button>
              </div>
              
              {/* Multiple Sessions */}
              <div className="space-y-6">
                {tryout.sessions.map((session, sessionIndex) => (
                  <div key={sessionIndex} className="p-4 border border-gray-100 rounded-md bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Evaluation Session {sessionIndex + 1}</h4>
                    
                    <div className="space-y-3">
                      {session.special_notes && (
                      <div className="flex items-center text-red-600">
                        <Info className="h-5 w-5 mr-2 text-red-500" />
                        <span>{session.special_notes}</span>
                      </div>
                      )}

                      <div className="flex items-center text-gray-600">
                        <Calendar className="h-5 w-5 mr-2 text-indigo-500" />
                        <span>{session.date}</span>
                      </div>
                      
                      <div className="flex items-center text-gray-600">
                        <Clock className="h-5 w-5 mr-2 text-indigo-500" />
                        <span>{session.time}</span>
                      </div>
                      
                      <div className="flex items-start text-gray-600">
                        <MapPin className="h-5 w-5 mr-2 text-indigo-500 mt-0.5" />
                        <div>
                          <p className="font-medium">{session.location.name}</p>
                          <p className="text-sm">{session.location.address}</p>
                        </div>
                      </div>
                      
                      {session.notes && (
                        <div className="mt-2 text-sm text-gray-600 italic">
                          {session.notes}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to process the database data into the frontend model
function processDataForFrontend(
  sessions: EvaluationSession[],
  ageGroups: AgeGroup[],
  sessionAgeGroups: EvaluationSessionAgeGroup[]
): TryoutEvent[] {
  // Create a map to group sessions by age group
  const ageGroupMap: Record<string, {
    ageGroup: AgeGroup,
    sessions: EvaluationSession[]
  }> = {};
  
  // For each join record, add the session to the corresponding age group
  sessionAgeGroups.forEach(joinRecord => {
    const ageGroup = ageGroups.find(ag => ag.id === joinRecord.age_group_id);
    const session = sessions.find(s => s.id === joinRecord.evaluation_session_id);
    
    if (ageGroup && session) {
      if (!ageGroupMap[ageGroup.id]) {
        ageGroupMap[ageGroup.id] = {
          ageGroup,
          sessions: []
        };
      }
      
      ageGroupMap[ageGroup.id].sessions.push(session);
    }
  });
  
  // Convert the map to our TryoutEvent array
  const events: TryoutEvent[] = Object.values(ageGroupMap).map(({ ageGroup, sessions }) => {
    // sort sessions by start_date
    sessions.sort((a, b) => new Date(a.session_date).getTime() - new Date(b.session_date).getTime());

    const formattedSessions: TryoutSession[] = sessions.map(session => {
      // Parse using utils.ts
      const date = parseDateString(session.session_date);
      const formattedDate = date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      
      // Format the time
      const startTime = formatTimeString(session.start_time);
      const endTime = formatTimeString(session.end_time);
      const timeRange = `${startTime} - ${endTime}`;
      
      return {
        id: session.id,
        date: formattedDate,
        time: timeRange,
        location: {
          name: session.location_name || 'TBD',
          address: session.location_address || 'Location to be determined'
        },
        notes: session.instructions || '',
        special_notes: session.special_notes || null
      };
    });
    
    return {
      ageGroups: [ageGroup.group_name],
      ageGroupIds: [ageGroup.id], // Store IDs for filtering
      sex: ageGroup.sex,
      birthDateRange: formatBirthDateRange(ageGroup.birth_date_min, ageGroup.birth_date_max),
      sessions: formattedSessions
    };
  });

  // Sort events by the Age Group name 
  events.sort((a, b) => a.ageGroups[0].localeCompare(b.ageGroups[0]));

  return events;
}

// Helper function to format time strings
function formatTimeString(timeString: string): string {
  try {
    // Parse the time string (expected format: 'HH:MM:SS')
    const [hours, minutes] = timeString.split(':').map(Number);
    
    // Create a date object to use the built-in formatting
    const date = new Date();
    date.setHours(hours, minutes, 0);
    
    // Format the time
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return timeString; // Return the original string if there's an error
  }
}

export default TryoutSchedule;
